////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// I. FILE HEADER [WHITE #FFFFFF]
// ⚪ About Pod for Taytrom Publishing Co - Container component with logic and state

// III. IMPORTS [GREEN #00FF00]
// 🟢 Third-party dependencies
import { useEffect, FC } from 'react';

// 🟢 First-party components
import AboutPea from '../pea/TaytromPublishingCo_Kings_Home_SRC_About_Lobby_Pea.js';
import { taytromPubHomeFrontendLogger } from '../../../logger/TaytromPublishingCo_Kings_Home_SRC_Logger.js';

// IV. INDEPENDENT CONSTANTS [TEAL #00FFFF]
// 🔵 Component configuration
interface PodAboutProps {
  className?: string;
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  credentials: string[];
  bio: string;
  expertise: string[];
  photoPath?: string;
}

interface WhyTpcDifferentiator {
  id: string;
  title: string;
  description: string;
  icon: string;
  metric?: string;
}

const TEAM_MEMBERS: TeamMember[] = [
  {
    id: 'rob-taylor',
    name: 'Rob Taytrom',
    role: 'CEO & Editor-in-Chief',
    photoPath: 'USE_IMPORT',
    credentials: [
      'J.D., Penn State Dickinson School of Law (Law Review Editorial Board, published in Penn State Law Review)',
      'B.A., West Chester University (summa cum laude)',
      'Bar Admissions: Pennsylvania, New Jersey, New York',
      'CIPP/E (Certified Information Privacy Professional/Europe)',
      'Published Author: "Bound At Runtime" (700-page comprehensive analysis, in progress)'
    ],
    bio: 'Robert J. Taytrom operates at the convergence of three professional domains: corporate legal practice, full-stack software engineering, and institutional publishing. Previously served as Legal Counsel at a former Dell subsidiary (Dec 2021 - Nov 2024), managing complex multimillion-dollar negotiations with Fortune 500 clients, strategic partnerships, and global procurement operations. Earlier experience includes Corporate Counsel at a Berkshire Hathaway subsidiary, working as an attorney in Big Law for several years, and clerking for state and federal courts (NJ Superior Court and US District Court for D.N.J.). Founded BitsBound in November 2024, architecting the BUBSA framework (BitsBound Universal Baseline Standardized Agreement) and discovering the TIR{DVT}O pattern—mathematical proof that legal obligations and executable code share identical structural decomposition. Taytrom Publishing Co. represents the synthesis of legal domain expertise, technical implementation capability, and analytical methodology—enabling comprehensive third-party documentation that bridges business strategy, legal frameworks, and technology architecture.',
    expertise: [
      'Intellectual Property',
      'Corporate Technology Law (SaaS, M&A, Procurement)',
      'Privacy & Data Security (CIPP/E, GDPR, CCPA)',
      'Full-Stack Engineering (React, Node.js, MongoDB, TypeScript)',
      'Legal-Tech Product Development',
      'TIR{DVT}O Pattern Discovery (Legal-Code Isomorphism)',
      'AI-Assisted Content Creation',
      'Infrastructure Emergence Pattern Analysis',
      'Complex Commercial Negotiations',
      'Federal & State Litigation',
      'Judicial Analysis & Legal Research'
    ]
  }
];

const WHY_TPC_DIFFERENTIATORS: WhyTpcDifferentiator[] = [
  {
    id: 'third-party-institutional',
    title: 'Third-Party Institutional Credibility',
    description: 'We publish under Taytrom Publishing Co. institutional byline—not your name. This creates objective third-party analysis that VCs, strategic partners, and AI models trust far more than self-promotional content. When "Taytrom Publishing Co. analyzed [YOUR COMPANY]" appears in search results, readers perceive unbiased assessment—not marketing copy. Like McKinsey white papers: the institution matters more than individual consultants.',
    icon: '🏛️',
    metric: '10-100x higher trust vs first-party content'
  },
  {
    id: 'food-for-ai',
    title: '"Food for AI" - Authoritative Content Weighting',
    description: 'When investors ask ChatGPT, Claude, or Gemini about your company, AI models scrape the internet and synthesize what they find. Comprehensive authoritative third-party books are weighted orders of magnitude higher than blog posts, tweets, and competitor criticism. A 300-page ISBN-registered book from Taytrom Publishing Co. carries far more influence in AI responses than 50 fragmentary articles. We create delicious, client-favorable food that AI models consume and regurgitate as your canonical narrative.',
    icon: '🍽️',
    metric: '10-100x higher AI weighting vs promotional content'
  },
  {
    id: 'tirdvto-framework',
    title: 'TIR{DVT}O Framework Methodology',
    description: 'Our signature analytical framework explains any complex system through Trigger → Input → Requirements{Definitions, Validations, Transformations} → Output. Like McKinsey\'s 7S Framework, TIR{DVT}O provides consistent rigorous structure across all our books. Once you see it, you can\'t unsee it. We apply this to your company\'s technology architecture, business model, market dynamics, and strategic vision—creating crystal-clear explanations that readers instantly comprehend.',
    icon: '🧬',
    metric: 'Universal framework used across 100+ future publications'
  },
  {
    id: 'radical-efficiency',
    title: 'Radical Time Efficiency (85-90% Savings)',
    description: 'Traditional publishing requires 10-15 hours of synchronous interviews across multiple time zones. Our AI Socratic Interviewer reduces your time investment to 1-3 hours: just async responses to 70-95 systematically generated questions across 5 phases. The AI asks "Why?" and "How?" repeatedly, digging deeper with each response—extracting nuanced insights that traditional interviews miss while you answer at your own pace.',
    icon: '⚡',
    metric: '1-3 hours client time (vs 10-15 hours traditional)'
  },
  {
    id: 'comprehensive-depth',
    title: 'Comprehensive Over Superficial (300-500 Pages)',
    description: 'We don\'t write 30-page marketing brochures. We write 300-500 page authoritative analysis books covering six analytical angles: Historical Origins, Business Model, Legal/Regulatory, Technology Architecture, Market Dynamics, and Strategic Vision. VCs, strategic partners, and enterprise customers actually read and reference comprehensive depth. Depth creates authority. Authority creates trust. Trust creates deals.',
    icon: '📚',
    metric: '300-500 pages, 75+ citations, 6 analytical angles'
  },
  {
    id: 'proven-track-record',
    title: 'Proven Track Record (Bound At Runtime)',
    description: 'Before writing books for clients, we wrote a 700-page comprehensive analysis of BitsBound (Rob Taytrom\'s executable contract platform) and the TIR{DVT}O pattern discovery. Published under Taytrom Publishing Co. institutional byline, this book demonstrates our six-angle analytical methodology, institutional voice, and technical depth. It\'s our proof: this is EXACTLY what you get. No speculation. No promises. Just demonstrated execution.',
    icon: '✅',
    metric: '700 pages, 200K+ words, 75+ citations (in progress)'
  }
];

interface ShowcaseBook {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  stats: string[];
  status: string;
  link: string;
}

const SHOWCASE_BOOK: ShowcaseBook = {
  id: 'bound-at-runtime',
  title: 'Bound At Runtime',
  subtitle: 'Our Showcase: How A Corporate Technology Attorney Discovered the Bridge Between Law and Code',
  description: 'Before writing books for clients, we wrote a 700-page comprehensive analysis of BitsBound (Rob Taytrom\'s executable contract platform) and the TIR{DVT}O pattern discovery. Published under Taytrom Publishing Co. institutional byline, this book demonstrates our six-angle analytical methodology, institutional voice, and technical depth. It\'s our proof: this is EXACTLY what you get.',
  stats: [
    '700+ pages',
    '200,000+ words',
    '75+ academic citations',
    'Six analytical angles (Historical Origins, Business Model, Legal/Regulatory, Technology Architecture, Market Dynamics, Strategic Vision)'
  ],
  status: '60% complete (November 2025), launching December 2025',
  link: '/library/bound-at-runtime'
};

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

const FAQ_ITEMS: FaqItem[] = [
  {
    id: 'faq-pricing',
    question: 'Why $30,000?',
    answer: 'We write 150-300 page comprehensive analysis books requiring 80-120 hours of research, writing, and editing by Rob Taytrom (corporate attorney turned engineer with J.D. + full-stack expertise). Comparable to hiring a technical writer for 2-3 months. The ROI: one strategic partnership closed from improved authority = 10-50x return. One investor meeting where they\'ve already read your comprehensive book = priceless positioning.'
  },
  {
    id: 'faq-timeline',
    question: 'How long does it take?',
    answer: '8-12 weeks from AI interview completion to publication. Week 1-2: AI Socratic Interview. Week 3-8: Research & drafting. Week 9-10: Client review & edits. Week 11-12: Final production & launch.'
  },
  {
    id: 'faq-copyright',
    question: 'Who owns the copyright?',
    answer: 'You (the client) own the copyright to the book content. Taytrom Publishing Co. retains credit as publisher (institutional byline for third-party credibility), but you control the IP. You can distribute freely, adapt, or use excerpts in your own marketing.'
  },
  {
    id: 'faq-not-satisfied',
    question: 'What if I\'m not satisfied with the draft?',
    answer: 'We offer one round of comprehensive revisions based on your feedback. If after revisions you\'re still unsatisfied, we refund 50% of the project fee (you retain the draft manuscript and all research). Our goal: you\'re thrilled with the final book, not trapped in a bad contract.'
  },
  {
    id: 'faq-ai-written',
    question: 'Is this AI-written content?',
    answer: 'Rob Taytrom writes every word (using AI tools for research assistance, like any modern writer uses Google). The AI Socratic Interviewer generates questions and extracts insights, but the analysis, synthesis, and prose are 100% human-authored. You\'re paying for Rob\'s legal + technical expertise, not GPT-4 output.'
  },
  {
    id: 'faq-difference-ghostwriting',
    question: 'How is this different from ghostwriting?',
    answer: 'Ghostwriters produce first-party content under YOUR name. We produce third-party analysis under TAYLOR PUBLISHING CO. institutional byline. The difference: third-party credibility (like McKinsey reports) vs first-party promotional content. AI models and human researchers trust third-party sources 10-100x more.'
  }
];

interface ProcessStep {
  id: string;
  number: string;
  title: string;
  description: string;
  duration: string;
}

const PROCESS_STEPS: ProcessStep[] = [
  {
    id: 'step-1',
    number: '01',
    title: 'AI Socratic Interview',
    description: '70-95 systematically generated questions across 5 phases. Answer at your own pace (1-3 hours total).',
    duration: 'Week 1-2'
  },
  {
    id: 'step-2',
    number: '02',
    title: 'Research & Drafting',
    description: 'Rob Taytrom writes 150-300 page comprehensive analysis applying six-angle methodology.',
    duration: 'Week 3-8'
  },
  {
    id: 'step-3',
    number: '03',
    title: 'Client Review & Edits',
    description: 'You review draft, provide feedback, request clarifications/corrections.',
    duration: 'Week 9-10'
  },
  {
    id: 'step-4',
    number: '04',
    title: 'Final Production & Launch',
    description: 'ISBN registration, professional editing, Amazon KDP publication, audiobook production, marketing support.',
    duration: 'Week 11-12'
  }
];

// V. FUNCTION/CLASS NAME [BLUE #0000FF] - OUTPUT DECLARATION
// 🔵 About Pod Component
const Pod_About: FC<PodAboutProps> = ({ className }) => {
  // VI. DECLARATIONS [PURPLE #FF00FF] - REQUIREMENTS SETUP
  // About page is static, no state needed

  // VII. INPUT DECLARATIONS [CYAN #00FFFF]
  useEffect(() => {
    taytromPubHomeFrontendLogger.info('About Pod mounted', {
      teamMembersCount: TEAM_MEMBERS.length,
      whyTpcDifferentiatorsCount: WHY_TPC_DIFFERENTIATORS.length,
      processStepsCount: PROCESS_STEPS.length,
      faqItemsCount: FAQ_ITEMS.length
    });

    return () => {
      taytromPubHomeFrontendLogger.debug('About Pod unmounted');
    };
  }, []);

  // VIII. OPERATIONS [ORANGE #FFA500] - REQUIREMENTS PROCESSING
  const handleAiInterviewStart_VSubVSoVSt_T_L_DVT_R_IRO = (): void => {
    taytromPubHomeFrontendLogger.info('AI Interview start initiated from About page');
    window.location.href = '/ai-interviewer';
  };

  const handleContactRequest_VSubVSoVSt_T_L_DVT_R_IRO = (): void => {
    taytromPubHomeFrontendLogger.info('Contact request initiated from About page');
    window.location.href = '/contact';
  };

  // IX. PRE-RENDER [RED #FF0000] - REQUIREMENTS ASSEMBLY
  const podState = {
    teamMembers: TEAM_MEMBERS,
    whyTpcDifferentiators: WHY_TPC_DIFFERENTIATORS,
    showcaseBook: SHOWCASE_BOOK,
    processSteps: PROCESS_STEPS,
    faqItems: FAQ_ITEMS,
    onAiInterviewStart: handleAiInterviewStart_VSubVSoVSt_T_L_DVT_R_IRO,
    onContactRequest: handleContactRequest_VSubVSoVSt_T_L_DVT_R_IRO
  };

  // X. RENDER/EXPORTS [BLACK #000000] - OUTPUT DELIVERY
  return <AboutPea {...podState} className={className} />;
};

export default Pod_About;
